import { styled } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import { colors, fonts, shadows } from "../../theme/theme";


// ---------------------------------------------------------------------------
// Page layout
// ---------------------------------------------------------------------------

export const PageWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "95%",
  maxWidth: 1180,
  marginTop: theme.spacing(3),
  marginLeft: "auto",
  marginRight: "auto",
  paddingBottom: theme.spacing(6),
}));

export const BreadcrumbRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const BreadcrumbText = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: "14px",
  fontWeight: 500,
  color: colors.textLight,
});

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: fonts.cinzel,
  fontWeight: 700,
  fontSize: "40px",
  color: colors.primary,
  letterSpacing: "1px",
  textTransform: "uppercase",
  [theme.breakpoints.down("md")]: { fontSize: "32px" },
  [theme.breakpoints.down("sm")]: { fontSize: "26px" },
}));

export const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: fonts.quicksand,
  fontSize: "16px",
  fontWeight: 400,
  color: colors.textLight,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(3),
}));

// ---------------------------------------------------------------------------
// Security tab – password card
// ---------------------------------------------------------------------------

// Two-pane settings layout: left nav rail + right content.
export const SettingsLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  alignItems: "flex-start",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

export const SettingsNav = styled(Box)(({ theme }) => ({
  width: 220,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  backgroundColor: colors.cardBg,
  borderRadius: "16px",
  padding: theme.spacing(1.5),
  boxShadow: shadows.soft,
  position: "sticky",
  top: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    width: "100%",
    flexDirection: "row",
    position: "static",
    overflowX: "auto",
  },
}));

export const NavItem = styled(Box, {
  shouldForwardProp: (p) => p !== "$active",
})(({ theme, $active }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: "10px",
  cursor: "pointer",
  fontFamily: fonts.quicksand,
  fontWeight: 600,
  fontSize: "15px",
  whiteSpace: "nowrap",
  color: $active ? colors.white : colors.primary,
  backgroundColor: $active ? colors.secondary : "transparent",
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&:hover": {
    backgroundColor: $active ? colors.secondary : "rgba(41, 64, 90, 0.06)",
  },
  "& .MuiSvgIcon-root": { fontSize: 20 },
}));

export const SettingsMain = styled(Box)({
  flex: 1,
  minWidth: 0,
  width: "100%",
});

export const SecurityCard = styled(Box)(({ theme }) => ({
  backgroundColor: colors.cardBg,
  borderRadius: "16px",
  padding: theme.spacing(4),
  boxShadow: shadows.soft,
}));

export const SecurityCardTitle = styled(Typography)({
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: "22px",
  color: colors.primary,
  marginBottom: 4,
});

export const SecurityCardSubtitle = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 400,
  fontSize: "14px",
  color: colors.textLight,
  marginBottom: 24,
});

export const PasswordRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  backgroundColor: colors.background,
  borderRadius: "12px",
  padding: theme.spacing(2, 2.5),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

export const PasswordIconBox = styled(Box)({
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: colors.secondary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& .MuiSvgIcon-root": { color: colors.white, fontSize: 24 },
});

export const PasswordInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
});

export const PasswordLabel = styled(Typography)({
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: "16px",
  color: colors.primary,
});

export const PasswordDate = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 400,
  fontSize: "13px",
  color: colors.textLight,
});

export const ResetPasswordBtn = styled(Button)({
  textTransform: "none",
  fontFamily: fonts.quicksand,
  fontWeight: 600,
  fontSize: "14px",
  borderRadius: "10px",
  padding: "8px 20px",
  backgroundColor: colors.secondary,
  color: colors.white,
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f2e3e", boxShadow: "none" },
});

// ---------------------------------------------------------------------------
// Reset password modal content
// ---------------------------------------------------------------------------

export const ModalBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
}));

export const ModalSubtitle = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 400,
  fontSize: "15px",
  color: colors.textLight,
  marginBottom: 20,
});

export const InfoBanner = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "#E6EBF0",
  borderRadius: "10px",
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2.5),
  "& .MuiSvgIcon-root": { color: colors.secondary, fontSize: 20 },
}));

export const InfoBannerText = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 500,
  fontSize: "14px",
  color: colors.secondary,
});

export const ModalButtonsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
}));

export const PasswordRequirements = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const RequirementsTitle = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 600,
  fontSize: "14px",
  color: colors.primary,
  marginBottom: 8,
});

export const RequirementItem = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 400,
  fontSize: "13px",
  color: colors.text,
  paddingLeft: 8,
  "&::before": { content: '"\\2022"', marginRight: 8 },
});
